import { baseApi } from 'src/Services/api.ts'

import { PaginatedResponse } from '../types'

export enum UserRole {
  SALES = 'SALES',
  ADMIN = 'ADMIN',
  LEAD_MANAGER = 'LEAD_MANAGER',
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export interface User {
  id: number
  email: string
  name: string
  role: UserRole
}

export interface UsersResponse {
  data: User[]
  success: boolean
}

export interface ManagedUser {
  id: number
  email: string
  firstName: string | null
  lastName: string | null
  phone: string | null
  role: UserRole
  status: UserStatus
  createdAt: string
}

export interface ManagedUsersQueryParams {
  pageNumber?: number
  limit?: number
  search?: string
  role?: UserRole
  status?: UserStatus
}

export interface CreateUserBody {
  email: string
  password: string
  firstName?: string
  lastName?: string
  phone?: string
  role: UserRole
}

export interface UpdateUserBody {
  email?: string
  firstName?: string
  lastName?: string
  phone?: string
}

export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllUsers: builder.query<UsersResponse, void>({
      query: () => '/users',
      providesTags: ['users'],
    }),

    getUsersForManagement: builder.query<
      PaginatedResponse<ManagedUser[]>,
      ManagedUsersQueryParams
    >({
      query: (params) => {
        const queryParams = new URLSearchParams()

        if (params) {
          if (params.pageNumber)
            queryParams.append('page', params.pageNumber.toString())
          if (params.limit) queryParams.append('limit', params.limit.toString())
          if (params.search) queryParams.append('search', params.search)
          if (params.role) queryParams.append('role', params.role)
          if (params.status) queryParams.append('status', params.status)
        }

        const queryString = queryParams.toString()
        return `/users/manage${queryString ? `?${queryString}` : ''}`
      },
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, newItems, meta) => {
        if (meta?.arg?.pageNumber === 1) {
          return {
            data: newItems?.data,
            meta: newItems.meta,
          }
        }
        return {
          data: [...currentCache.data, ...newItems.data],
          meta: newItems.meta,
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg)
      },
      providesTags: ['users'],
    }),

    createUser: builder.mutation<void, CreateUserBody>({
      query: (data) => ({
        url: '/users',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['users'],
    }),

    updateUser: builder.mutation<void, { id: number; data: UpdateUserBody }>({
      query: ({ id, data }) => ({
        url: `/users/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['users'],
    }),

    changeUserRole: builder.mutation<void, { id: number; role: UserRole }>({
      query: ({ id, role }) => ({
        url: `/users/${id}/role`,
        method: 'PATCH',
        body: { role },
      }),
      invalidatesTags: ['users'],
    }),

    resetUserPassword: builder.mutation<void, { id: number; password: string }>(
      {
        query: ({ id, password }) => ({
          url: `/users/${id}/password`,
          method: 'PATCH',
          body: { password },
        }),
        invalidatesTags: ['users'],
      },
    ),

    archiveUser: builder.mutation<void, number>({
      query: (id) => ({
        url: `/users/${id}/archive`,
        method: 'PATCH',
        body: {},
      }),
      invalidatesTags: ['users'],
    }),

    restoreUser: builder.mutation<void, number>({
      query: (id) => ({
        url: `/users/${id}/restore`,
        method: 'PATCH',
        body: {},
      }),
      invalidatesTags: ['users'],
    }),
  }),
  overrideExisting: true,
})

export const {
  useGetAllUsersQuery,
  useGetUsersForManagementQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useChangeUserRoleMutation,
  useResetUserPasswordMutation,
  useArchiveUserMutation,
  useRestoreUserMutation,
} = usersApi
